import React, { createContext, useContext, ReactNode } from 'react';
import { toast, Toaster } from 'sonner';
import './NotificationContext.css';
import * as Sentry from '@sentry/react';

interface NotificationContextType {
  showSuccess: (message: string, description?: string) => void;
  showError: (message: string, error: Error, description?: string) => void;
  showInfo: (message: string, description?: string) => void;
  showWarning: (message: string, description?: string) => void;
  showLoading: (message: string, description?: string) => string | number;
  dismiss: (toastId?: string | number) => void;
  dismissAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const showSuccess = (message: string, description?: string) => {
    toast.success(message, {
      description,
      duration: 3000,
    });
  };

  const showError = (message: string, error: Error, description?: string) => {
    Sentry.captureException(error);
    toast.error(message, {
      description,
      duration: 4000,
    });
  };

  const showInfo = (message: string, description?: string) => {
    toast.info(message, {
      description,
      duration: 3000,
    });
  };

  const showWarning = (message: string, description?: string) => {
    toast.warning(message, {
      description,
      duration: 4000,
    });
  };

  const showLoading = (message: string, description?: string): string | number => {
  return toast.loading(message, {
      description,
    });
  };

  const dismiss = (toastId?: string | number) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  const dismissAll = () => {
    toast.dismiss();
  };

  const value: NotificationContextType = {
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showLoading,
    dismiss,
    dismissAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Toaster
        position="bottom-left"
        expand={false}
        richColors={false}
        closeButton={false}
        gap={8}
        toastOptions={{
          style: {
            background: 'transparent',
            color: 'inherit',
          },
          className: 'notification-toast',
        }}
      />
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
